<template lang="pug">
.field
  .label(v-if="label") {{ label }}

  //- Used inside AppForm
  FormField(v-if="name" v-slot="$field" :name="name")
    Select.w-full(
      v-model="$field.value"
      :options="dropdownOptions"
      optionGroupLabel="groupLabel"
      optionGroupChildren="items"
      optionLabel="name"
      :optionValue="optionValue"
      filter
      scrollHeight="32rem"
      placeholder="Choose Service Type"
    )
      //- Group Header
      template(#optiongroup="{ option }")
        .text-canvas-400.font-semibold.pt-3.w-full {{ option.groupLabel }}

      //- Options
      template(#option="{ option }")
        .flex-between.gap-3.w-full
          .text-md.flex-center.gap-2
            i.ph.ph-fill.ph-circle.text-xs.text-canvas-300
            span {{ option.name }}
          .flex.gap-1
            Tag(:value="option.serviceCode" severity="info")
            Tag(:value="format.startCase(option.scheduleType)" severity="secondary")

      //- Selected Value / Placeholder
      template(#value="{ value, placeholder }")
        .flex-between.gap-3.w-full(v-if="value")
          .text-md {{ selectedOption(value)?.name }}
          .flex.gap-1
            Tag(:value="selectedOption(value)?.serviceCode" severity="info")
            Tag(:value="format.startCase(selectedOption(value)?.scheduleType)" severity="secondary")
        span(v-else) {{ placeholder }}
    Message(v-if="$field?.invalid && ($field?.touched || formSubmitted)" severity="error" size="small" variant="simple") {{ $field.error?.message }}
  //- Used standalone, outside AppForm
  Select.w-full(
    v-else
    v-model="model"
    :options="dropdownOptions"
    optionGroupLabel="groupLabel"
    optionGroupChildren="items"
    optionLabel="name"
    :optionValue="optionValue"
    filter
    scrollHeight="32rem"
    placeholder="Choose Service Type"
  )
    //- Group Header
    template(#optiongroup="{ option }")
      .text-canvas-400.font-semibold.pt-3.w-full {{ option.groupLabel }}

    //- Options
    template(#option="{ option }")
      .flex-between.gap-3.w-full
        .text-md.flex-center.gap-2
          i.ph.ph-fill.ph-circle.text-xs.text-canvas-300
          span {{ option.name }}
        .flex.gap-1
          Tag(:value="option.serviceCode" severity="info")
          Tag(:value="format.startCase(option.scheduleType)" severity="secondary")

    //- Selected Value / Placeholder
    template(#value="{ value, placeholder }")
      .flex-between.gap-3.w-full(v-if="value")
        .text-md {{ selectedOption(value)?.name }}
        .flex.gap-1
          Tag(:value="selectedOption(value)?.serviceCode" severity="info")
          Tag(:value="format.startCase(selectedOption(value)?.scheduleType)" severity="secondary")
      span(v-else) {{ placeholder }}

  Message(v-if="error" severity="error" size="small" variant="simple") {{ error }}
  //- pre model: {{ model }}
  //- pre dropdownOptions: {{ dropdownOptions }}
</template>

<script setup>
import { ref, inject, onMounted, computed } from 'vue'
import Select from 'primevue/select'
import { FormField } from '@primevue/forms'
import Message from 'primevue/message'
import { dropdownOptionsApi } from '@/api'
import { format, utils } from '@/utils'

const formSubmitted = inject('appFormSubmitted', ref(false))

const apiResponse = ref([])
const error = ref()
const loading = ref(false)
const model = defineModel({ required: false })

const props = defineProps({
  name:        { type: String, required: false, default: null },
  label:       { type: String, required: false, default: null },
  // Set to e.g. "id" to bind a scalar (matches AppForm fields like tourTypeId).
  // Leave unset to bind the whole service option object (used in EmailPreviews.vue).
  optionValue: { type: String, required: false, default: null },
  // The tour's current tour_type_id when editing an existing tour. Passed
  // through to the API so an archived service still shows up as an option.
  tourTypeId:  { type: [String, Number], required: false, default: null },
})

const dropdownOptions = computed(() => {
  let grouped = Object.groupBy(apiResponse.value, (option) => option.serviceType)
  grouped = Object.entries(grouped)
  grouped = utils.sortBy(grouped, ([groupLabel, items]) => groupLabel)
  // console.log(grouped)
  grouped = grouped.map(([groupLabel, items]) => {
    return {
      groupLabel: format.startCase(groupLabel),
      items:      utils.sortBy(items, ({ name }) => name),
    }
  })
  return grouped
})

// When optionValue is set, the bound value is a scalar (e.g. an id) rather
// than the full option object, so look the option back up for display.
function selectedOption(value) {
  if (!props.optionValue) return value

  return dropdownOptions.value
    .flatMap((group) => group.items)
    .find((option) => option[props.optionValue] === value)
}

onMounted(async () => {
  try {
    loading.value = true
    const params = props.tourTypeId ? { tourTypeId: props.tourTypeId } : {}
    apiResponse.value = await dropdownOptionsApi.getServices(params).then(({ data }) => data)
  } catch(err) {
    error.value = err
  } finally {
    loading.value = false
  }
})
</script>
